<template>
  <grid
    ref="itemsGrid"
    :list-url="listUrl"
    loading-message="Loading report ..."
    grid-wrapper-css-class=""
    grid-header-css-class=""
    :always-show-loading-indicator="true"
    :initial-filters="initialFilters"
    :perform-data-fetches="false"
    @data-loaded="gridLoadedData"
    @will-fetch="gridWillFetch"
    @meta-loaded="gridMetaLoaded"
  >
    <template v-slot:table="table">
      <div
        v-if="showSendEmail || showDownloadReport"
        class="mb-4"
      >
        <a
          v-if="showDownloadReport && downloadReport"
          :href="downloadReport.url"
          class="btn btn-success btn-sm"
        >
          {{ downloadReport.title }}
        </a>
        <button
          v-if="showSendEmail && sendEmail"
          type="button"
          class="btn btn-success btn-sm"
          @click.prevent="doSendEmail"
        >
          {{ sendEmail.title }}
        </button>
        <loading-indicator
          v-if="isSendingEmail"
          :inline="true"
          additional-css-class="ps-2"
          message="Sending email ..."
        />
      </div>
      <div
        v-for="(item, itemIdx) of table.data"
        :key="`table-item-${itemIdx}`"
        class="card digest-card"
      >
        <div class="card-header bg-light fw-bold">{{ item.attributes.title }}</div>
        <div
          v-if="item.attributes.view_mode == SectionViewMode.Empty"
          class="empty-section"
        >
          {{ item.attributes.empty_message }}
        </div>
        <table
          v-else-if="item.attributes.view_mode == SectionViewMode.EventsList"
          class="table table-bordered table-sm table-hover"
        >
          <thead>
          <tr>
            <th>Number</th>
            <th>Name</th>
            <th>Dates</th>
            <th class="tight"/>
          </tr>
          </thead>
          <tbody>
          <template
            v-for="(sectionEvent, sectionEventIdx) of sectionEvents(item)"
            :key="`section-event-item-${sectionEventIdx}`"
          >
            <tr>
              <td>{{ sectionEvent.number }}</td>
              <td>{{ sectionEvent.name }}</td>
              <td>{{ sectionEvent.dateRange }}</td>
              <td class="tight px-1">
                <a
                  v-if="isNonEmptyString(sectionEvent.viewUrl)"
                  :href="sectionEvent.viewUrl"
                  class="fw-bold"
                >
                  view
                </a>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
        <table
          v-else
          class="table table-bordered table-sm table-hover"
        >
          <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Function</th>
            <th>Country</th>
          </tr>
          </thead>
          <tbody>
          <template
            v-for="(sectionEvent, sectionEventIdx) of sectionEvents(item)"
            :key="`section-event-item-${sectionEventIdx}`"
          >
            <tr>
              <td
                colspan="4"
                class="event"
              >
                <a :href="sectionEvent.viewUrl">Event: {{ sectionEvent.number }}, Date: {{ sectionEvent.dateRange }}</a>
                <template v-if="item.attributes.view_invitation">
                  (<a
                  href="#"
                  @click.prevent="showInvitationEmail(sectionEvent)"
                >
                  View invitation email
                </a>)
                </template>
              </td>
            </tr>
            <tr
              v-for="(participant, participantIdx) of sectionEvent.participants"
              :key="`section-event-participant-item-${participantIdx}`"
              :class="[participant.isCanceled ? 'table-danger' : null]"
            >
              <td>{{ participant.name }} <span v-if="participant.isCanceled">Canceled at {{
                  participant.canceledAt
                }}</span></td>
              <td>{{ participant.email }}</td>
              <td>{{ participant.function }}</td>
              <td>{{ participant.country }}</td>
            </tr>
            <tr
              v-if="!sectionEvent.isLast"
              :key="`section-event-item-spacer-${sectionEventIdx}`"
              class="event-spacer"
            >
              <td colspan="4"/>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
    </template>
  </grid>
  <teleport
    to="#vue-portal-target"
    append
  >
    <modal
      ref="viewInvitationModal"
      modal-title="Invitation e-mail"
      modal-class="wide-modal"
      :form-url="modalFormUrl"
    />
  </teleport>
</template>
<script setup lang="ts">
import {computed, onMounted, ref, useTemplateRef} from 'vue'
import Grid from './grid.vue'
import LoadingIndicator from './loading-indicator.vue'
import Modal from './modal.vue'
import { isNonEmptyString, isPresent } from '../js-utils'
import axios from 'axios'
import Rails from '@rails/ujs'

enum SectionViewMode {
  Empty = 0,
  EventsList = 1,
  Participants = 2
}

interface ReportAction {
  url: string;
  title: string;
}

interface SectionEventParticipant {
  name: string;
  email: string;
  function: string;
  country: string;
  isCanceled: boolean;
  canceledAt: Date | null;
}

interface SectionEvent {
  id: number;
  number: string;
  name: string;
  viewUrl: string;
  dateRange: string;
  participants: SectionEventParticipant[];
  isLast: boolean;
  viewInvitationUrl: string;
}

const props = defineProps({
  listUrl: {default: null, type: String},
  initialFilters: {default: null, type: String},
  viewInvitationUrl: {default: null, type: String}
})

const itemsGrid = useTemplateRef<InstanceType<typeof Grid> | null>("itemsGrid")
const viewInvitationModal = useTemplateRef<InstanceType<typeof Modal> | null>("viewInvitationModal")

const jsonApiData = ref<any>(null as any | null)
const sendEmail = ref<ReportAction | null>(null)
const isSendingEmail = ref(false)
const modalFormUrl = ref<string | null>(null)
const downloadReport = ref<ReportAction | null>(null)

const showSendEmail = computed((): boolean => {
  return isNonEmptyString(sendEmail.value?.url) && isNonEmptyString(sendEmail.value?.title)
})

const showDownloadReport = computed((): boolean => {
  return isNonEmptyString(downloadReport.value?.url) && isNonEmptyString(downloadReport.value?.title)
})

onMounted(() => {
  const invitationUrl = props.viewInvitationUrl
  if (isNonEmptyString(invitationUrl)) {
    showViewInvitationEmail(invitationUrl)
  }
})

const gridLoadedData = (aJsonApiData: any): void => {
  jsonApiData.value = aJsonApiData
}

const gridWillFetch = (_url: string, params: any, initialFetch: boolean): void => {
  if (!initialFetch) {
    const newUrl: string[] = []
    if (isPresent(params.filter_date)) {
      newUrl.push(`filter_date=${encodeURIComponent(params.filter_date)}`)
    }
    if (isPresent(params.filter_ncp)) {
      newUrl.push(`filter_ncp=${encodeURIComponent(params.filter_ncp)}`)
    }
    if (isPresent(params.filter_event)) {
      newUrl.push(`filter_event=${encodeURIComponent(params.filter_event)}`)
    }
    if (isPresent(params.filter_status)) {
      newUrl.push(`filter_status=${encodeURIComponent(params.filter_status)}`)
    }
    // history.pushState({ filterParams: params }, 'Activity digest', `?${newUrl.join('&')}`)
    window.location.href = `?${newUrl.join('&')}`
  }
}

const gridMetaLoaded = (metadata: any): void => {
  if (isPresent(metadata.sendEmail)) {
    sendEmail.value = metadata.sendEmail
  }
  if (isPresent(metadata.downloadReport)) {
    downloadReport.value = metadata.downloadReport
  }
}

const sectionEvents = (section: any): SectionEvent[] => {
  const events: SectionEvent[] = []
  for (const eventRel of section.relationships.events.data) {
    if (!jsonApiData.value) {
      continue
    }

    const includedItems = jsonApiData.value.included as any[]
    if (!Array.isArray(includedItems)) {
      continue
    }

    const item = includedItems.find(fitem => (fitem.type === 'activity_digest_report_section_events' && fitem.id === eventRel.id))
    if (item) {
      const participants = []
      for (const participantRel of item.relationships.participants.data) {
        const participant = includedItems.find(fitem => (fitem.type === 'activity_digest_report_participants' && fitem.id === participantRel.id))

        if (participant) {
          participants.push({
            name: participant.attributes.name,
            email: participant.attributes.email,
            function: participant.attributes.function,
            country: participant.attributes.country,
            isCanceled: isPresent(participant.canceled_at),
            canceledAt: participant.canceled_at
          })
        }
      }

      events.push({
        id: item.id,
        number: item.attributes.number,
        name: item.attributes.name,
        viewUrl: item.links.show,
        dateRange: item.attributes.date_range,
        participants: participants,
        isLast: false,
        viewInvitationUrl: item.links.view_invitation
      })
    }
  }
  if (events.length > 0) {
    events[events.length - 1].isLast = true
  }
  return events
}

const doSendEmail = (): void => {
  const emailUrl = sendEmail.value?.url
  if (isNonEmptyString(emailUrl)) {
    isSendingEmail.value = true
    itemsGrid.value?.clearError()
    axios.post(emailUrl, {}, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': Rails.csrfToken()
      },
    })
      .catch((error) => {
        itemsGrid.value?.showError(`An error occurred while sending email: ${error.message}`)
      })
      .then(() => {
        isSendingEmail.value = false
      })
  }
}

const showInvitationEmail = (sectionEvent: SectionEvent): void => {
  showViewInvitationEmail(sectionEvent.viewInvitationUrl)
}

const showViewInvitationEmail = (url: string | null): void => {
  if (isNonEmptyString(url)) {
    modalFormUrl.value = url
    viewInvitationModal.value?.showModal()
  }
}
</script>
